import type {
  UseMutationOptions,
  UseMutationResult,
} from '@tanstack/react-query'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'

interface CopyToClipboardMutationOptions extends Omit<
  UseMutationOptions<void, Error, void>,
  'mutationFn'
> {
  value: string
}

export function useCopyValueToClipboard({
  value,
  ...mutationOptions
}: CopyToClipboardMutationOptions): [
  () => void,
  UseMutationResult<void, Error, void>,
] {
  const _copyToClipboard = useMutation({
    ...mutationOptions,
    mutationFn: async () => {
      return navigator.clipboard.writeText(value)
    },

    onError: (error, ...rest) => {
      toast.error(`Failed to copy to clipboard: ${error}`)
      return mutationOptions.onError?.(error, ...rest)
    },
  })

  const copyToClipboard = () => {
    _copyToClipboard.mutate()
  }

  return [copyToClipboard, _copyToClipboard]
}

export function useCopyToClipboard(
  mutationOptions?: UseMutationOptions<void, Error, string>,
): [(value: string) => void, UseMutationResult<void, Error, string>] {
  const _copyToClipboard = useMutation({
    ...mutationOptions,
    mutationFn: async (value: string) => {
      return navigator.clipboard.writeText(value)
    },

    onError: (error, ...rest) => {
      toast.error(`Failed to copy to clipboard: ${error}`)
      return mutationOptions?.onError?.(error, ...rest)
    },
  })

  return [_copyToClipboard.mutate, _copyToClipboard]
}
